import { apiClient } from "../utils/api";

const API_BASE_URL = import.meta.env.VITE_API_URL || "http://localhost:8000";

class OnboardingService {
  constructor() {
    this.baseURL = `${API_BASE_URL}/api/onboarding`;
    this.maxRetries = 3;
    this.retryDelay = 1000;
    this._cachedProfileId = null;
  }

  getAuthHeaders() {
    const token = localStorage.getItem("accessToken");
    return {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    };
  }

  /**
   * Retry wrapper for network requests
   */
  async withRetry(operation, retries = this.maxRetries) {
    try {
      return await operation();
    } catch (error) {
      if (retries > 0 && this.isRetryableError(error)) {
        await this.delay(this.retryDelay);
        return this.withRetry(operation, retries - 1);
      }
      throw this.handleError(error);
    }
  }

  /**
   * Check if error is retryable (network issues, 5xx errors)
   */
  isRetryableError(error) {
    if (!error.response) return true; // Network error
    const status = error.response.status;
    return status >= 500 || status === 408 || status === 429;
  }

  /**
   * Delay utility for retry logic
   */
  delay(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  /**
   * Enhanced error handling with user-friendly messages
   */
  handleError(error) {
    const errorMap = {
      400: "Invalid data provided. Please check your input.",
      401: "Authentication required. Please log in again.",
      403: "You do not have permission to perform this action.",
      404: "The requested resource was not found.",
      409: "A conflict occurred. The data may have been modified.",
      422: "Validation failed. Please check your input.",
      429: "Too many requests. Please try again later.",
      500: "Server error. Please try again later.",
      502: "Service temporarily unavailable. Please try again.",
      503: "Service temporarily unavailable. Please try again.",
    };

    if (!error.response) {
      return new Error("Network error. Please check your internet connection.");
    }

    const status = error.response.status;
    const message = errorMap[status] || "An unexpected error occurred.";

    const enhancedError = new Error(message);
    enhancedError.originalError = error;
    enhancedError.status = status;
    enhancedError.data = error.response.data;

    return enhancedError;
  }

  /**
   * Initialize onboarding process for a user
   */
  async initializeOnboarding(data = {}) {
    return this.withRetry(async () => {
      // First try to get existing profile
      try {
        const existingProfile = await this.getCurrentProfile();
        if (existingProfile) {
          return {
            profile: existingProfile,
            currentStep: existingProfile.current_step || 1,
            stepData: existingProfile.step_data || {},
            isCompleted: existingProfile.is_completed || false,
          };
        }
      } catch (error) {
        // Profile doesn't exist, continue to create one
      }

      // Create new profile if none exists
      const response = await apiClient.post(`${this.baseURL}/profiles/`, data);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const profile = await response.json();
      this._cachedProfileId = profile.id;
      return {
        profile: profile,
        currentStep: profile.current_step || 1,
        stepData: profile.step_data || {},
        isCompleted: profile.is_completed || false,
      };
    });
  }

  async getCurrentProfile() {
    return this.withRetry(async () => {
      const response = await apiClient.get(`${this.baseURL}/profiles/current/`);

      if (!response.ok) {
        if (response.status === 404) {
          return null;
        }
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const profile = await response.json();
      if (profile?.id) this._cachedProfileId = profile.id;
      return profile;
    });
  }

  /**
   * Get adaptive onboarding flow based on user profile
   */
  async getAdaptiveFlow() {
    return this.withRetry(async () => {
      const response = await apiClient.get(
        `${this.baseURL}/profiles/adaptive_flow/`
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      return response.json();
    });
  }

  /**
   * Update step progress with auto-save functionality
   */
  async updateStepProgress(profileId, stepData) {
    return this.withRetry(async () => {
      const response = await apiClient.post(
        `${this.baseURL}/profiles/${profileId}/update_step/`,
        {
          ...stepData,
          timestamp: new Date().toISOString(),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      return response.json();
    });
  }

  /**
   * Complete onboarding process
   */
  async completeOnboarding(data = {}) {
    return this.withRetry(async () => {
      const profile = await this.getCurrentProfile();
      if (!profile) {
        throw new Error("No onboarding profile found");
      }

      const response = await apiClient.post(
        `${this.baseURL}/profiles/${profile.id}/complete/`,
        {
          ...data,
          completed_at: new Date().toISOString(),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      return response.json();
    });
  }

  /**
   * Save step data with debouncing for auto-save
   */
  async saveStepData(profileId, stepId, data) {
    return this.withRetry(async () => {
      const response = await apiClient.patch(
        `${this.baseURL}/profiles/${profileId}/`,
        {
          current_step: stepId,
          step_data: data,
          last_updated: new Date().toISOString(),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      return response.json();
    });
  }

  /**
   * Get recommendations for a specific profile
   */
  async getRecommendations(profileId) {
    return this.withRetry(async () => {
      const response = await apiClient.get(
        `${this.baseURL}/profiles/${profileId}/recommendations/`
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      return response.json();
    });
  }

  /**
   * Get onboarding steps for current user
   */
  async getOnboardingSteps() {
    return this.withRetry(async () => {
      const response = await apiClient.get(`${this.baseURL}/steps/for_user/`);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      return response.json();
    });
  }

  /**
   * Get personalized recommendations
   */
  async getPersonalizedRecommendations() {
    return this.withRetry(async () => {
      const response = await apiClient.get(`${this.baseURL}/recommendations/`);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      return response.json();
    });
  }

  /**
   * Dismiss a recommendation
   */
  async dismissRecommendation(recommendationId) {
    return this.withRetry(async () => {
      const response = await apiClient.post(
        `${this.baseURL}/recommendations/${recommendationId}/dismiss/`
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      return response.json();
    });
  }

  /**
   * Accept a recommendation
   */
  async acceptRecommendation(recommendationId) {
    return this.withRetry(async () => {
      const response = await apiClient.post(
        `${this.baseURL}/recommendations/${recommendationId}/accept/`
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      return response.json();
    });
  }

  /**
   * Get onboarding analytics
   */
  async getAnalytics() {
    return this.withRetry(async () => {
      const response = await apiClient.get(
        `${this.baseURL}/analytics/completion_stats/`
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      return response.json();
    });
  }

  /**
   * Track onboarding events for analytics
   */
  async trackEvent(eventData) {
    try {
      return this.withRetry(async () => {
        const response = await apiClient.post(
          `${this.baseURL}/analytics/track_event/`,
          {
            ...eventData,
            timestamp: new Date().toISOString(),
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        return response.json();
      });
    } catch (error) {
      // Silently fail analytics tracking to not disrupt user experience
      console.warn("Analytics tracking failed:", error.message);
      return null;
    }
  }

  /**
   * Check if user has completed onboarding
   */
  async checkOnboardingStatus() {
    try {
      const profile = await this.getCurrentProfile();
      return {
        hasProfile: !!profile,
        isCompleted: profile?.onboarding_completed || false,
        currentStep: profile?.current_step || 0,
      };
    } catch (error) {
      return {
        hasProfile: false,
        isCompleted: false,
        currentStep: 0,
      };
    }
  }

  /**
   * Validate step data (client-side validation)
   */
  isStepValid(stepId, stepData) {
    if (!stepData || typeof stepData !== "object") {
      return stepId === 1; // Welcome step is always valid even without data
    }

    // Get current step data for this specific step
    const currentStepData = stepData[stepId] || {};

    // Basic validation based on step
    switch (stepId) {
      case 1: // Welcome step
        return true; // No validation needed for welcome
      case 2: // Family info step
        return !!(
          currentStepData.family_situation && currentStepData.number_of_children
        );
      case 3: // Child info step
        return (
          currentStepData.skipped === true ||
          !!(currentStepData.name && currentStepData.date_of_birth)
        );
      case 4: // Preferences step
        return true; // Optional step, no required fields
      case 5: // Features step
        return true; // Optional step, no required fields
      case 6: // Completion step
        return true; // No validation needed for completion
      default:
        return true; // Default to valid for unknown steps
    }
  }

  /**
   * Validate step data (async version for server-side validation)
   */
  async validateStep(stepId, stepData) {
    // For now, use client-side validation
    // In the future, this could make an API call for server-side validation
    return this.isStepValid(stepId, stepData);
  }

  /**
   * Validate all steps before completion
   */
  async validateAllSteps(allStepData) {
    if (Object.keys(allStepData).length === 0)
      return true;

    // Check each required step
    const requiredSteps = [2, 3]; // Family info and child info are required

    for (const stepId of requiredSteps) {
      if (!this.isStepValid(stepId, allStepData)) {
        return false;
      }
    }

    return true;
  }

  async saveProgress(progressData) {
    return this.withRetry(async () => {
      if (!this._cachedProfileId) {
        const profile = await this.getCurrentProfile();
        if (!profile) throw new Error("No onboarding profile found");
      }

      const updatePayload = {
        current_step: progressData.currentStep,
        step_data: progressData.stepData,
        last_updated: new Date().toISOString(),
      };

      const response = await apiClient.patch(
        `${this.baseURL}/profiles/${this._cachedProfileId}/`,
        updatePayload
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      return response.json();
    });
  }
}

export default new OnboardingService();
